import { useNavigate } from "react-router-dom";
import type { OpenClaw } from "../../types/openclaw";
import styles from "./DashboardEmpty.module.css";

type DashboardEmptyProps = {
  readonly pending: OpenClaw | null;
  readonly bookCallUrl: string | null;
  // false when the caller has no instance budget (a sharing collaborator
  // who never purchased) — onboarding is replaced by the purchase CTA.
  readonly canStart: boolean;
  readonly purchaseUrl: string | null;
};

export const DashboardEmpty = ({
  pending,
  bookCallUrl,
  canStart,
  purchaseUrl,
}: DashboardEmptyProps): JSX.Element => {
  const navigate = useNavigate();

  if (pending !== null) {
    return (
      <div className={styles.shell}>
        <section className={styles.card} aria-labelledby="empty-title">
          <span className={styles.eyebrow}>// PROVISIONING</span>
          <h2 id="empty-title" className={styles.title}>
            We're setting up your instance
          </h2>
          <p className={styles.body}>
            Your <strong>{pending.name}</strong> agent is provisioning in the
            background. It'll appear here as soon as it's ready to hand over.
          </p>
          {bookCallUrl !== null && bookCallUrl.length > 0 ? (
            <a
              className={styles.cta}
              href={bookCallUrl}
              target="_blank"
              rel="noreferrer"
            >
              Book your onboarding call
              <span aria-hidden="true">→</span>
            </a>
          ) : null}
        </section>
      </div>
    );
  }

  if (!canStart) {
    return (
      <div className={styles.shell}>
        <section className={styles.card} aria-labelledby="empty-title">
          <span className={styles.eyebrow}>// GET STARTED</span>
          <h2 id="empty-title" className={styles.title}>
            Get your own OpenClaw
          </h2>
          <p className={styles.body}>
            Your account doesn't include its own instance yet. Purchase an
            OpenClaw to launch a managed AI workspace of your own — or ask a
            teammate to share theirs with you.
          </p>
          {purchaseUrl !== null && purchaseUrl.length > 0 ? (
            <a
              className={styles.cta}
              href={purchaseUrl}
              target="_blank"
              rel="noreferrer"
            >
              Get your own OpenClaw
              <span aria-hidden="true">→</span>
            </a>
          ) : null}
        </section>
      </div>
    );
  }

  return (
    <div className={styles.shell}>
      <section className={styles.card} aria-labelledby="empty-title">
        <span className={styles.eyebrow}>// GET STARTED</span>
        <h2 id="empty-title" className={styles.title}>
          Launch your first OpenClaw
        </h2>
        <p className={styles.body}>
          An OpenClaw is your managed AI workspace in the cloud. We'll ask a few
          quick questions to tailor it to how you'll use it.
        </p>
        <button
          type="button"
          className={styles.cta}
          onClick={() => navigate("/dashboard/onboarding")}
        >
          Start onboarding
          <span aria-hidden="true">→</span>
        </button>
      </section>
    </div>
  );
};
